/** Precise frame extraction at native resolution. Mobile-safe (no ffmpeg required). */

import { canvasToBlob } from './canvas';

export interface FrameExtractOpts {
  /** 0..1 along the timeline, or absolute seconds. */
  at: number;
  asSeconds?: boolean;
  /** Output mime; PNG keeps quality, jpeg/webp are smaller. */
  mime?: 'image/png' | 'image/jpeg' | 'image/webp';
  quality?: number;
}

export async function extractFrame(videoBlob: Blob, opts: FrameExtractOpts): Promise<{ blob: Blob; width: number; height: number; tSec: number }> {
  const url = URL.createObjectURL(videoBlob);
  try {
    const v = document.createElement('video');
    v.src = url;
    v.crossOrigin = 'anonymous';
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    await new Promise<void>((res, rej) => {
      v.onloadedmetadata = () => res();
      v.onerror = () => rej(new Error('video metadata load failed'));
    });
    const tSec = opts.asSeconds ? opts.at : opts.at * v.duration;
    await seekTo(v, tSec);
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
    const blob = await canvasToBlob(c, opts.mime ?? 'image/png', opts.quality ?? 0.95);
    return { blob, width: c.width, height: c.height, tSec };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function seekTo(v: HTMLVideoElement, tSec: number): Promise<void> {
  return new Promise((res, rej) => {
    let settled = false;
    const onSeeked = () => {
      if (settled) return;
      settled = true;
      v.removeEventListener('seeked', onSeeked);
      // Some browsers fire seeked before the frame is fully decoded.
      if ('requestVideoFrameCallback' in v) {
        (v as any).requestVideoFrameCallback(() => res());
      } else {
        setTimeout(res, 30);
      }
    };
    v.addEventListener('seeked', onSeeked);
    v.onerror = () => { if (!settled) { settled = true; rej(new Error('seek failed')); } };
    try { v.currentTime = Math.max(0, Math.min(v.duration - 0.001, tSec)); }
    catch (e) { rej(e as Error); }
  });
}

/** Convenience: grab first + last frames for start/end frame workflows. */
export async function extractEndpoints(videoBlob: Blob): Promise<{ start: Blob; end: Blob; width: number; height: number; durationSec: number }> {
  const url = URL.createObjectURL(videoBlob);
  const v = document.createElement('video');
  v.src = url; v.muted = true; v.playsInline = true;
  await new Promise<void>((res, rej) => { v.onloadedmetadata = () => res(); v.onerror = () => rej(new Error('meta')); });
  URL.revokeObjectURL(url);
  const start = (await extractFrame(videoBlob, { at: 0, asSeconds: true })).blob;
  const end = (await extractFrame(videoBlob, { at: Math.max(0, v.duration - 0.04), asSeconds: true })).blob;
  return { start, end, width: v.videoWidth, height: v.videoHeight, durationSec: v.duration };
}
